/**
 * create_schedule Tool - Create a recurring test schedule
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';
import type { CreateJobScheduleRequest } from '@runhuman/shared';
import { FREQUENCY_LABELS } from '@runhuman/shared';

export interface CreateScheduleArgs extends CreateJobScheduleRequest {
  projectId: string;
}

export const createScheduleToolDefinition: Tool = {
  name: 'create_schedule',
  description: `Create a recurring test schedule that automatically runs a template.

Schedules run a template at a specified time and frequency. Supported frequencies:
- daily: runs every day
- weekly: runs on selected weekdays (requires weekdays array)
- biweekly: runs every other week on selected weekdays (requires weekdays array)
- monthly: runs on a specific day each month (requires dayOfMonth)
- once: runs once on a specific date (requires scheduledDate)

Time is specified in 24h format with quarter-hour precision (minutes: 0, 15, 30, 45).
Timezone must be a valid IANA timezone string (e.g., "America/New_York", "Europe/London").

Steps:
1. Use list_templates to find the template ID
2. Call create_schedule with the template ID, frequency, and time settings
3. The schedule will automatically create test jobs at the specified times`,
  inputSchema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'Project ID (required).',
      },
      name: {
        type: 'string',
        description: 'Schedule name (required).',
      },
      templateId: {
        type: 'string',
        description: 'Template ID to run on each execution (required). Get from list_templates.',
      },
      frequency: {
        type: 'string',
        enum: ['daily', 'weekly', 'biweekly', 'monthly', 'once'],
        description: 'How often to run. Default: weekly.',
      },
      scheduledHour: {
        type: 'number',
        description: 'Hour of day (0-23). Default: 9.',
        minimum: 0,
        maximum: 23,
      },
      scheduledMinute: {
        type: 'number',
        description: 'Minute of hour (0, 15, 30, or 45). Default: 0.',
        enum: [0, 15, 30, 45],
      },
      timezone: {
        type: 'string',
        description: 'IANA timezone (e.g., "America/New_York", "Europe/London"). Default: America/New_York.',
      },
      weekdays: {
        type: 'array',
        items: { type: 'number', minimum: 0, maximum: 6 },
        description: 'Days of week for weekly/biweekly (0=Sun, 1=Mon, ..., 6=Sat). Required for weekly/biweekly.',
      },
      dayOfMonth: {
        type: 'number',
        description: 'Day of month for monthly (1-28, or 0 for last day). Required for monthly.',
        minimum: 0,
        maximum: 28,
      },
      scheduledDate: {
        type: 'string',
        description: 'Specific date for one-time schedules (YYYY-MM-DD). Required for once.',
      },
      urlOverride: {
        type: 'string',
        description: 'URL override (replaces the template default URL).',
      },
      autoCreateGithubIssuesOverride: {
        type: 'boolean',
        description: 'Auto-create GitHub issues from test results.',
      },
      autoCreateOnlyTesterSurfacedOverride: {
        type: 'boolean',
        description: 'Override the template default for filing only tester-surfaced issues (skip telemetry-only).',
      },
      reopenIssuesOnDuplicateOverride: {
        type: 'boolean',
        description: 'Override the template default for reopening closed issues on a duplicate match. Defaults to leaving closed duplicates closed.',
      },
      commentOnDuplicateOverride: {
        type: 'boolean',
        description: 'Override the template default for posting a cross-reference comment when a finding matches an existing issue. Omit to inherit the template default.',
      },
    },
    required: ['projectId', 'name', 'templateId', 'scheduledHour', 'scheduledMinute', 'timezone'],
  },
};

export async function handleCreateSchedule(
  args: CreateScheduleArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const { projectId, ...scheduleData } = args;
    const schedule = await jobClient.createSchedule(projectId, scheduleData);

    const freq = FREQUENCY_LABELS[schedule.frequency];
    const time = `${String(schedule.scheduledHour).padStart(2, '0')}:${String(schedule.scheduledMinute).padStart(2, '0')}`;

    return {
      content: [{
        type: 'text',
        text: `Schedule Created!

Name: ${schedule.name}
Schedule ID: ${schedule.id}
Frequency: ${freq}
Time: ${time} ${schedule.timezone}
Status: ${schedule.status}
Next Run: ${schedule.nextExecutionAt}

Manage with:
- update_schedule(projectId: "${projectId}", scheduleId: "${schedule.id}", ...)
- delete_schedule(projectId: "${projectId}", scheduleId: "${schedule.id}")`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to create schedule\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
